import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Hero = () => {
  const headlineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headlineRef.current;
    if (!el) return;
    const items = el.querySelectorAll<HTMLElement>('.h-anim');
    items.forEach((item, i) => {
      item.style.opacity = '0';
      item.style.transform = 'translateY(36px)';
      setTimeout(() => {
        item.style.transition = 'opacity 1s cubic-bezier(.16,1,.3,1), transform 1s cubic-bezier(.16,1,.3,1)';
        item.style.opacity = '1';
        item.style.transform = 'translateY(0)';
      }, 200 + i * 130);
    });
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: '#080808' }}>

      {/* Full bleed background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/0aea57d44745cf999678784335fd2620.jpg"
          alt="Art Den Studio creative experience"
          className="w-full h-full object-cover"
          style={{ opacity: 0.28 }}
        />
        {/* Gradient overlays for depth */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(8,8,8,0.97) 0%, rgba(8,8,8,0.75) 45%, rgba(8,8,8,0.4) 100%)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-48" style={{ background: 'linear-gradient(to top, #080808, transparent)' }} />
      </div>

      {/* Grain texture */}
      <div className="absolute inset-0 z-1 pointer-events-none opacity-40"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")", backgroundSize: '200px 200px' }}
      />

      {/* Paint splatter accents */}
      <div className="absolute top-24 right-12 w-64 h-64 pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(255,67,34,0.12) 0%, transparent 70%)', borderRadius: '63% 37% 54% 46% / 55% 48% 52% 45%' }} />
      <div className="absolute bottom-32 right-1/3 w-48 h-48 pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(255,179,0,0.10) 0%, transparent 70%)', borderRadius: '38% 62% 46% 54% / 60% 44% 56% 40%' }} />

      {/* Stacked image cards — right side */}
      <div className="absolute right-0 top-0 bottom-0 w-2/5 hidden lg:flex flex-col justify-center items-end pr-10 gap-5">
        {/* Card 1 */}
        <div className="relative w-full max-w-[280px] h-48 rounded-2xl overflow-hidden" style={{ transform: 'rotate(2.5deg)', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
          <img
            src="https://readdy.ai/api/search-image?query=child%20hands%20holding%20paintbrush%20close%20up%20colorful%20orange%20yellow%20paint%20on%20canvas%20bright%20vibrant%20joyful%20creative%20art%20class%20studio%20warm%20light&width=560&height=380&seq=hero-card-kids-v3&orientation=landscape"
            alt="Kids art classes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: '#ff4322' }} />
            <span className="font-outfit text-white text-xs font-semibold">Kids Classes</span>
          </div>
        </div>

        {/* Card 2 */}
        <div className="relative w-full max-w-[240px] h-40 rounded-2xl overflow-hidden ml-auto" style={{ transform: 'rotate(-1.5deg)', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
          <img
            src="https://readdy.ai/api/search-image?query=adults%20evening%20painting%20class%20friends%20laughing%20creating%20together%20wine%20glasses%20canvases%20art%20studio%20warm%20atmospheric%20lighting%20cinematic%20social%20creative%20night%20out%20premium&width=480&height=340&seq=hero-card-adult-v3&orientation=landscape"
            alt="Adult paint nights"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: '#ffb300' }} />
            <span className="font-outfit text-white text-xs font-semibold">Adult Events</span>
          </div>
        </div>

        {/* Card 3 */}
        <div className="relative w-full max-w-[260px] h-44 rounded-2xl overflow-hidden" style={{ transform: 'rotate(1deg)', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
          <img
            src="https://readdy.ai/api/search-image?query=birthday%20party%20art%20studio%20kids%20celebrating%20painting%20together%20colorful%20decorations%20balloons%20canvases%20vibrant%20joyful%20celebration%20energy%20premium%20photography&width=520&height=360&seq=hero-card-party-v3&orientation=landscape"
            alt="Birthday parties"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: '#ff7120' }} />
            <span className="font-outfit text-white text-xs font-semibold">Private Parties</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div ref={headlineRef} className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-24 lg:pr-[46%]">

        {/* Label */}
        <div className="h-anim flex items-center gap-3 mb-8">
          <div className="h-px w-8" style={{ background: '#ff4322' }} />
          <span className="font-outfit text-xs font-semibold tracking-widest3 uppercase text-artden-coral">Nazareth, PA · Art Studio</span>
        </div>

        {/* Main headline */}
        <h1 className="h-anim font-cormorant font-medium text-white leading-[0.95] tracking-tight mb-8" style={{ fontSize: 'clamp(3.5rem, 7vw, 6rem)' }}>
          Not Just Art.<br />
          <em className="font-light italic" style={{ color: '#ff4322' }}>An Experience.</em>
        </h1>

        <p className="h-anim font-outfit text-white/60 text-lg max-w-md leading-relaxed mb-10">
          Classes, parties & creative experiences for kids and adults in the heart of Nazareth, PA. All supplies included. No experience needed.
        </p>

        {/* Primary CTAs */}
        <div className="h-anim flex flex-col sm:flex-row items-start gap-4 mb-8">
          <a
            href="https://artdenstudio.com/calendar/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-artden-coral hover:brightness-110 text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap inline-flex items-center gap-2.5 text-base"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-event-line"></i>
            </span>
            View Classes
          </a>
          <Link
            to="/parties-events"
            className="border border-white/25 hover:border-white/60 text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap inline-flex items-center gap-2.5 text-base"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-cake-3-line"></i>
            </span>
            Book a Party
          </Link>
        </div>

        {/* Supporting CTAs */}
        <div className="h-anim flex flex-wrap items-center gap-6">
          <a
            href="https://app.getoccasion.com/p/n/3mrk3shx9p7bhxywpcu9cg"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-artden-amber hover:brightness-110 text-sm font-outfit font-medium transition-all whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-gift-2-line"></i>
            </span>
            Buy Gift Card
          </a>
          <span className="w-1 h-1 rounded-full bg-white/15" />
          <a
            href="/contact"
            className="flex items-center gap-2 text-white/40 hover:text-white/80 text-sm font-outfit font-medium transition-all whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-chat-1-line"></i>
            </span>
            Ask a Question
          </a>
        </div>

        {/* Stats row */}
        <div className="h-anim flex flex-wrap items-center gap-8 mt-12 pt-10 border-t border-white/8">
          <div>
            <p className="font-cormorant text-3xl font-semibold text-white">5.0★</p>
            <p className="font-outfit text-white/40 text-xs mt-0.5">100+ Five-Star Reviews</p>
          </div>
          <div className="w-px h-8 bg-white/10" />
          <div>
            <p className="font-cormorant text-3xl font-semibold text-white">All Ages</p>
            <p className="font-outfit text-white/40 text-xs mt-0.5">Kids, Teens & Adults</p>
          </div>
          <div className="w-px h-8 bg-white/10" />
          <div>
            <p className="font-cormorant text-3xl font-semibold text-white">Nazareth</p>
            <p className="font-outfit text-white/40 text-xs mt-0.5">37 E Belvidere St, PA</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
